import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Square, Mic, Users, Clock, History, Check, X, QrCode, MessageCircle, ChevronDown, ChevronUp, Music, ListMusic, TrendingUp, Radio } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import QRCode from 'qrcode.react';

const MobileKaraokeLiveDashboard = ({
  isLive,
  currentSession,
  queue = [],
  pendingRequests = [],
  historyRequests = [],
  sessionStats,
  onStartSession,
  onStopSession,
  onApproveRequest,
  onDenyRequest,
  onMarkAsDone,
  onAnnounceRequest,
  onRemoveFromQueue,
  getPublicUrl,
  profile,
  currency,
  format,
  user
}) => {
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'queue', 'history', 'stats'
  const [expandedRequestId, setExpandedRequestId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedRequestId(prev => prev === id ? null : id);
  };

  // Format time utility
  const formatTime = (dateObj) => {
    if (!dateObj) return '';
    try {
      const d = dateObj.toDate ? dateObj.toDate() : new Date(dateObj);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const renderRequestCard = (req, type) => {
    const isExpanded = expandedRequestId === req.id;

    return (
      <motion.div
        key={req.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="mb-3 bg-gray-900 border border-purple-500/30 rounded-xl overflow-hidden"
      >
        <div 
          className="p-3 flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpand(req.id)}
        >
          <div className="flex-1 min-w-0 pr-3">
            <h4 className="font-bold text-white truncate text-base leading-tight mb-0.5">{req.song_title}</h4>
            <p className="text-gray-400 text-xs truncate mb-1">{req.artist_name}</p>
            {/* Singer / requester only (not song title or track artist) — Pending + Queue + History */}
            <div className="flex items-center text-[14px] font-bold text-white leading-snug">
              <Mic className="w-4 h-4 mr-1 shrink-0 opacity-90" aria-hidden />
              <span className="truncate font-bold">
                {req.requester_name || req.name || req.requesterName || '—'}
              </span>
            </div>
          </div>
          
          <div className="flex flex-col items-end justify-center">
            <span className="text-[10px] text-gray-500 mb-1">{formatTime(req.created_at)}</span>
            {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden bg-black/40 border-t border-purple-500/20"
            >
              <div className="p-3 space-y-2">
                {req.note && (
                  <div className="bg-gray-800 p-2 rounded-lg flex items-start text-xs">
                    <MessageCircle className="w-3 h-3 text-purple-400 mr-1.5 mt-0.5 shrink-0" />
                    <span className="text-gray-300 italic leading-tight">"{req.note}"</span>
                  </div>
                )}

                <div className="flex gap-1.5 mt-2">
                  {type === 'pending' && (
                    <>
                      <Button
                        onClick={(e) => { e.stopPropagation(); onDenyRequest(req.id); }}
                        variant="destructive"
                        className="flex-1 h-10 px-0 rounded-lg text-xs"
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); onApproveRequest(req.id); }}
                        className="flex-1 h-10 px-0 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs"
                      >
                        <Check className="w-4 h-4 mr-1" /> Accept
                      </Button>
                    </>
                  )}

                  {type === 'queue' && (
                    <>
                      <Button
                        onClick={(e) => { e.stopPropagation(); onAnnounceRequest && onAnnounceRequest(req); }}
                        variant="outline"
                        className="flex-1 h-10 px-0 border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10 rounded-lg text-[11px] sm:text-xs tracking-tight"
                      >
                        <Radio className="w-3.5 h-3.5 mr-1" /> Announce
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); onRemoveFromQueue(req); }}
                        variant="outline"
                        className="flex-1 h-10 px-0 border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-lg text-[11px] sm:text-xs tracking-tight"
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Remove
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); onMarkAsDone(req); }}
                        className="flex-1 h-10 px-0 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[11px] sm:text-xs tracking-tight"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Done
                      </Button>
                    </>
                  )}
                  
                  {type === 'history' && (
                    <div className="w-full text-center py-1">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider ${
                        req.status === 'performed' ? 'bg-blue-500/20 text-blue-400' :
                        req.status === 'denied' ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {req.status === 'performed' ? 'Performed' : 
                         req.status === 'denied' ? 'Rejected' : req.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-black text-white relative">
      
      {/* Header / Status Bar */}
      <div className="px-4 pt-4 pb-2 bg-gray-900 border-b border-purple-900/50 z-10 sticky top-0 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center">
              Karaoke Dashboard
            </h1>
            <div className="flex items-center mt-1">
              <div className={`w-2 h-2 rounded-full mr-2 ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
              <span className="text-sm text-gray-400">{isLive ? 'Session Active' : 'Offline'}</span>
            </div>
          </div>
          
          <Button
            onClick={isLive ? onStopSession : onStartSession}
            variant={isLive ? 'destructive' : 'default'}
            className={isLive ? 'bg-red-600' : 'bg-purple-600 hover:bg-purple-700'}
            size="sm"
          >
            {isLive ? <Square className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {isLive ? 'End Session' : 'Go Live'}
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-4 pb-24 hide-scrollbar">
        {!isLive && activeTab !== 'stats' && activeTab !== 'history' && (
          <div className="flex flex-col items-center justify-center h-full text-center opacity-70 mt-10">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Mic className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">You are offline</h3>
            <p className="text-sm text-gray-400 max-w-xs">
              Go live to start receiving karaoke requests from your audience.
            </p>
            <Button onClick={onStartSession} className="mt-6 bg-purple-600 text-white rounded-full px-8">
              Start Session
            </Button>
          </div>
        )}

        {isLive && activeTab === 'pending' && (
          <div>
            {pendingRequests.length === 0 ? (
              <div className="text-center py-12 opacity-60">
                <Music className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                <p className="text-gray-400">No pending requests</p>
              </div>
            ) : (
              <AnimatePresence>
                {pendingRequests.map(req => renderRequestCard(req, 'pending'))}
              </AnimatePresence>
            )}
          </div>
        )}

        {isLive && activeTab === 'queue' && (
          <div>
            {queue.length === 0 ? (
              <div className="text-center py-12 opacity-60">
                <Mic className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                <p className="text-gray-400">Queue is empty</p>
                <p className="text-xs mt-1 text-gray-500">Approve pending requests to add them here</p>
              </div>
            ) : (
              <AnimatePresence>
                {queue.map(req => renderRequestCard(req, 'queue'))}
              </AnimatePresence>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            {historyRequests.length === 0 ? (
              <div className="text-center py-12 opacity-60">
                <History className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                <p className="text-gray-400">No session history yet</p>
              </div>
            ) : (
              <AnimatePresence>
                {historyRequests.map(req => renderRequestCard(req, 'history'))}
              </AnimatePresence>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-4 pb-20">
            <Card className="bg-gray-900 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mr-3">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Session Stats</p>
                    <p className="text-white font-medium">Current Performance</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-black/50 p-3 rounded-lg border border-gray-800">
                    <p className="text-xs text-gray-500 mb-1">Total Requests</p>
                    <p className="text-2xl font-bold text-white">{sessionStats?.totalRequests || 0}</p>
                  </div>
                  <div className="bg-black/50 p-3 rounded-lg border border-gray-800">
                    <p className="text-xs text-gray-500 mb-1">Songs Performed</p>
                    <p className="text-2xl font-bold text-blue-400">{sessionStats?.songsPerformed || 0}</p>
                  </div>
                  <div className="bg-black/50 p-3 rounded-lg border border-gray-800">
                    <p className="text-xs text-gray-500 mb-1">Session Time</p>
                    <p className="text-2xl font-bold text-yellow-400">{sessionStats?.sessionTime || 0}m</p>
                  </div>
                  <div className="bg-black/50 p-3 rounded-lg border border-gray-800">
                    <p className="text-xs text-gray-500 mb-1">Total Earned</p>
                    <p className="text-2xl font-bold text-green-400">{format(sessionStats?.totalEarnings || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-purple-500/20">
              <CardContent className="p-4 text-center">
                <h3 className="text-lg font-bold text-white mb-2">QR Code</h3>
                <p className="text-sm text-gray-400 mb-4">Let your audience scan to request songs</p>
                <div className="bg-white p-4 rounded-xl inline-block mx-auto">
                  {getPublicUrl() ? (
                    <QRCode
                      value={getPublicUrl()}
                      size={150}
                      level="H"
                      includeMargin={false}
                    />
                  ) : (
                    <div className="w-[150px] h-[150px] bg-gray-200 flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    className="w-full border-purple-600 text-purple-300"
                    onClick={() => {
                      if (getPublicUrl()) {
                        navigator.clipboard.writeText(getPublicUrl());
                        toast({ title: "Copied to clipboard" });
                      }
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Fixed Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] bg-gray-900 border-t border-purple-900/50 pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.5)]">
        <div className="flex items-center justify-around h-16 px-2">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex flex-col items-center justify-center flex-1 h-full relative ${
              activeTab === 'pending' ? 'text-purple-400' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <div className="relative">
              <Clock className="w-5 h-5 mb-1" />
              {pendingRequests.length > 0 && (
                <span className="absolute -top-2 -right-3 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 border-2 border-gray-900">
                  {pendingRequests.length}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium">Pending</span>
          </button>
          
          <button
            onClick={() => setActiveTab('queue')}
            className={`flex flex-col items-center justify-center flex-1 h-full relative ${
              activeTab === 'queue' ? 'text-purple-400' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <div className="relative">
              <ListMusic className="w-5 h-5 mb-1" />
              {queue.length > 0 && (
                <span className="absolute -top-2 -right-3 min-w-[18px] h-[18px] flex items-center justify-center bg-blue-500 text-white text-[10px] font-bold rounded-full px-1 border-2 border-gray-900">
                  {queue.length}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium">Queue</span>
          </button>

          <button
            onClick={() => setActiveTab('history')}
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              activeTab === 'history' ? 'text-purple-400' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <History className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">History</span>
          </button>

          <button
            onClick={() => setActiveTab('stats')}
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              activeTab === 'stats' ? 'text-purple-400' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <TrendingUp className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">Stats</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileKaraokeLiveDashboard;